import { Fragment, useState } from "react";
import { SegmentedControl, Loader, Stack, Text, Group } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { classifyRisk } from "@iso27001/shared";
import { apiFetch } from "../../api/client.js";
import { useEnumLabel } from "../../i18n/enum-label.js";

interface RisikenMatrix {
    before: Record<string, number>;
    after: Record<string, number>;
    total: number;
}

// Background color tokens for each risk class, using Mantine CSS variables
const CLASS_BG: Record<"mäßig" | "hoch" | "kritisch", string> = {
    "mäßig":    "var(--mantine-color-green-light)",
    "hoch":     "var(--mantine-color-yellow-light)",
    "kritisch": "var(--mantine-color-red-light)",
};

const CLASS_COLOR: Record<"mäßig" | "hoch" | "kritisch", string> = {
    "mäßig":    "var(--mantine-color-green-light-color)",
    "hoch":     "var(--mantine-color-yellow-light-color)",
    "kritisch": "var(--mantine-color-red-light-color)",
};

const AXIS_CELL_SIZE = 32; // px — matches grid-template-columns first column

export function MatrixGrid() {
    const [mode, setMode] = useState<"before" | "after">("before");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { label } = useEnumLabel();

    const { data, isLoading } = useQuery({
        queryKey: ["risiken", "matrix"],
        queryFn: () => apiFetch<RisikenMatrix>("/api/risiken/matrix"),
    });

    if (isLoading || !data) return <Loader />;

    const matrix = data[mode];

    // Likelihood 5→1 (top to bottom), Damage 1→5 (left to right)
    const likelihoods = [5, 4, 3, 2, 1];
    const damages = [1, 2, 3, 4, 5];

    // Count legend totals across active matrix
    const legendCounts: Record<"mäßig" | "hoch" | "kritisch", number> = { "mäßig": 0, "hoch": 0, "kritisch": 0 };
    for (const [key, count] of Object.entries(matrix)) {
        const [l, d] = key.split(",").map(Number) as [number, number];
        const klass = classifyRisk(l * d);
        if (klass) legendCounts[klass] += count;
    }

    return (
        <Stack gap="md">
            <SegmentedControl
                value={mode}
                onChange={(val) => setMode(val as "before" | "after")}
                data={[
                    { value: "before", label: t("risiken.matrix.mode.before") },
                    { value: "after",  label: t("risiken.matrix.mode.after") },
                ]}
                style={{ alignSelf: "flex-start" }}
            />

            {/* Y-axis label (rotated, positioned left of grid) */}
            <Group gap={0} align="flex-start" wrap="nowrap">
                {/* Rotated y-axis label */}
                <div style={{
                    width: AXIS_CELL_SIZE,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    // Height of the 5 rows (each ~56px)
                    height: 5 * 56,
                }}>
                    <Text
                        size="xs"
                        c="dimmed"
                        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", userSelect: "none" }}
                    >
                        {t("risiken.matrix.axis.likelihood")}
                    </Text>
                </div>

                <Stack gap={0}>
                    {/* Grid: first column = y-axis numbers, then 5 data columns */}
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: `${AXIS_CELL_SIZE}px repeat(5, 1fr)`,
                        width: AXIS_CELL_SIZE + 5 * 80,
                    }}>
                        {likelihoods.map((l) => (
                            <Fragment key={l}>
                                {/* Y-axis number */}
                                <div
                                    key={`y-${l}`}
                                    style={{
                                        width: AXIS_CELL_SIZE,
                                        height: 56,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: 600,
                                        fontSize: "0.8rem",
                                        color: "var(--mantine-color-dimmed)",
                                        borderRight: "1px solid var(--mantine-color-default-border)",
                                    }}
                                >
                                    {l}
                                </div>

                                {/* 5 cells for this likelihood row */}
                                {damages.map((d) => {
                                    const key = `${l},${d}`;
                                    const count = matrix[key] ?? 0;
                                    const klass = classifyRisk(l * d);
                                    const bg = klass ? CLASS_BG[klass] : "transparent";
                                    const color = klass ? CLASS_COLOR[klass] : "inherit";
                                    return (
                                        <div
                                            key={key}
                                            onClick={() => klass && navigate(`/risiken?riskClass=${klass}`)}
                                            style={{
                                                height: 56,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: bg,
                                                color,
                                                cursor: klass ? "pointer" : "default",
                                                border: "1px solid var(--mantine-color-default-border)",
                                                fontWeight: count > 0 ? 700 : 400,
                                                fontSize: "1rem",
                                                transition: "filter 0.15s",
                                            }}
                                            title={klass ? `${t("risiken.field.class")}: ${label("riskClass", klass)}` : undefined}
                                            onMouseEnter={(e) => { if (klass) (e.currentTarget as HTMLDivElement).style.filter = "brightness(0.9)"; }}
                                            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.filter = ""; }}
                                        >
                                            {count > 0 ? count : ""}
                                        </div>
                                    );
                                })}
                            </Fragment>
                        ))}
                    </div>

                    {/* X-axis numbers row (below the grid, aligned to columns) */}
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: `${AXIS_CELL_SIZE}px repeat(5, 1fr)`,
                        width: AXIS_CELL_SIZE + 5 * 80,
                        marginTop: 2,
                    }}>
                        <div /> {/* empty corner */}
                        {damages.map((d) => (
                            <div
                                key={`x-${d}`}
                                style={{
                                    textAlign: "center",
                                    fontSize: "0.8rem",
                                    color: "var(--mantine-color-dimmed)",
                                    fontWeight: 600,
                                }}
                            >
                                {d}
                            </div>
                        ))}
                    </div>

                    {/* X-axis label */}
                    <Text size="xs" c="dimmed" ta="center" mt={2}>
                        {t("risiken.field.damage")}
                    </Text>
                </Stack>
            </Group>

            {/* Legend */}
            <Group gap="md">
                {(["mäßig", "hoch", "kritisch"] as const).map((klass) => (
                    <Group
                        key={klass}
                        gap={6}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/risiken?riskClass=${klass}`)}
                    >
                        <div style={{
                            width: 14,
                            height: 14,
                            borderRadius: 3,
                            background: CLASS_BG[klass],
                            border: "1px solid var(--mantine-color-default-border)",
                        }} />
                        <Text size="sm">{label("riskClass", klass)}</Text>
                        <Text size="sm" fw={600}>{legendCounts[klass]}</Text>
                    </Group>
                ))}
                <Text size="sm" c="dimmed">{t("risiken.matrix.total", { total: data.total })}</Text>
            </Group>
        </Stack>
    );
}
